import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { LocalizedLink } from './LocalizedLink';
import { useLocale } from '../contexts/LocaleContext';
import { ARTICLE_MENU_GROUPS } from '../data/articlesNav';
import { LEGAL_FOOTER_COPYRIGHT } from '../data/legalCompanyInfo';
import { localizePath } from '../utils/localePaths';

const RESOURCE_LINKS = [
  { to: '/guides/', labelKey: 'guides' },
  { to: '/secteurs/', labelKey: 'sectors' },
  { to: '/comparatifs/', labelKey: 'comparisons' },
  { to: '/cas-clients/', labelKey: 'caseStudies' },
  { to: '/faq/', labelKey: 'faq' },
  { to: '/roi/', labelKey: 'roi' },
  { to: '/a-propos/', labelKey: 'about' },
  { to: '/glossaire/', labelKey: 'glossary' },
];

const LEGAL_LINKS = [
  { to: '/legal/mentions/', labelKey: 'mentions' },
  { to: '/legal/cgu/', labelKey: 'cgu' },
  { to: '/legal/cgv/', labelKey: 'cgv' },
  { to: '/legal/privacy/', labelKey: 'privacy' },
  { to: '/legal/rgpd/', labelKey: 'rgpd' },
];

function FooterLink({ to, children, className = 'public-site-footer__link' }) {
  return (
    <LocalizedLink to={to} className={className}>
      {children}
    </LocalizedLink>
  );
}

function FooterHomeAnchor({ id, children }) {
  const location = useLocation();
  const { localePrefix } = useLocale();
  const homePath = localizePath('/', localePrefix);
  const isHome = location.pathname === homePath || location.pathname === `${homePath}/`;
  const className = 'public-site-footer__link';

  if (isHome) {
    return (
      <a href={`#${id}`} className={className}>
        {children}
      </a>
    );
  }

  return (
    <Link to={`${homePath}#${id}`} className={className}>
      {children}
    </Link>
  );
}

function FooterLinkList({ items, renderItem }) {
  return (
    <ul className="list-unstyled small mb-0 public-site-footer__list">
      {items.map((item) => (
        <li key={item.to || item.id} className="public-site-footer__list-item">
          {renderItem(item)}
        </li>
      ))}
    </ul>
  );
}

export default function PublicSiteFooter() {
  const { t } = useTranslation(['landing', 'common']);

  const sectorHubLinks = useMemo(() => {
    const secteursGroup = ARTICLE_MENU_GROUPS.find((group) => group.groupKey === 'secteurs');
    if (!secteursGroup) {
      return [];
    }

    return secteursGroup.items
      .filter((item) => item.to !== '/secteurs/')
      .map((item) => ({
        to: item.to,
        label: t(`common:articles.${item.articleKey}`),
      }));
  }, [t]);

  const productLinks = useMemo(
    () => [
      { id: 'home', to: '/', label: t('landing:footer.internal.home') },
      { id: 'features', anchor: 'features', label: t('landing:footer.internal.features') },
      { id: 'pricing', to: '/tarifs/', label: t('landing:footer.internal.pricing') },
      { id: 'register', to: '/register/', label: t('landing:footer.internal.register') },
      { id: 'login', to: '/login/', label: t('landing:footer.internal.login') },
    ],
    [t]
  );

  return (
    <footer className="public-site-footer bg-dark text-white mt-auto">
      <Container className="public-site-footer__main py-4 py-lg-5">
        <Row className="g-4">
          <Col xs={12} sm={6} lg={4}>
            <h2 className="h6 fw-bold mb-3">
              <i className="bi bi-camera-video-fill me-2" aria-hidden="true" />
              {t('common:brand')}
            </h2>
            <p className="small opacity-75 mb-4">{t('landing:footer.tagline')}</p>
            <h3 className="h6 fw-bold mb-3">{t('landing:footer.product')}</h3>
            <FooterLinkList
              items={productLinks}
              renderItem={(item) => {
                if (item.anchor) {
                  return <FooterHomeAnchor id={item.anchor}>{item.label}</FooterHomeAnchor>;
                }
                return <FooterLink to={item.to}>{item.label}</FooterLink>;
              }}
            />
          </Col>

          <Col xs={12} sm={6} lg={4}>
            <h3 className="h6 fw-bold mb-3">{t('landing:footer.resources')}</h3>
            <FooterLinkList
              items={RESOURCE_LINKS}
              renderItem={(item) => (
                <FooterLink to={item.to}>{t(`landing:footer.resourceLinks.${item.labelKey}`)}</FooterLink>
              )}
            />
          </Col>

          <Col xs={12} sm={6} lg={4}>
            <h3 className="h6 fw-bold mb-3">{t('landing:footer.legal')}</h3>
            <FooterLinkList
              items={LEGAL_LINKS}
              renderItem={(item) => (
                <FooterLink to={item.to}>{t(`landing:footer.legalLinks.${item.labelKey}`)}</FooterLink>
              )}
            />
          </Col>
        </Row>

        <div className="public-site-footer__sectors mt-4 pt-4">
          <p className="small text-uppercase fw-semibold opacity-75 mb-3">{t('landing:footer.sectorHubs')}</p>
          <ul className="list-unstyled small mb-0 public-site-footer__sector-list">
            {sectorHubLinks.map((item) => (
              <li key={item.to} className="public-site-footer__sector-item">
                <FooterLink to={item.to}>{item.label}</FooterLink>
              </li>
            ))}
          </ul>
        </div>
      </Container>

      <div className="public-site-footer__copyright py-3">
        <Container>
          <p className="small text-center mb-0 opacity-75">{LEGAL_FOOTER_COPYRIGHT}</p>
        </Container>
      </div>
    </footer>
  );
}
